import { toRaw } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import {
  getBetslipLogDetails,
  TicketResolveError,
  useBetslipNotificationEvents,
} from '@nsftx/seven-client-retail-vue-sgp'
import { logService } from '@nsftx/seven-client-retail-utils'
import type { BetslipView } from '@nsftx/seven-client-retail-vue-integrator'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import { type PrintContextPayinSlip } from '@nsftx/seven-client-retail-print'
import errorParser from '@/utils/errorParser'
import { t } from '@/plugins/translationsPlugin'
import { SLIP_ACTION_PENDING_NOTIFICATION_ID } from '../slip-checker/slipCheckerService'
import printService from '../print/services/printService'
import { recordBetslipPayinTransaction } from '@/modules/calculator/calculatorTransactionService'
const LOG_PREFIX = '[slipPayinService]'

const registerSlipPayinHandlers = () => {
  const { registerBetslipEventHandlers } = useBetslipNotificationEvents()

  registerBetslipEventHandlers({
    onConfirmPlaceFinished: handleSlipPayinSuccess,
  })
}

const handleSlipPayinSuccess = async (betslip: BetslipView) => {
  const { showNotification, closeNotificationById } = useNotificationsStore()

  logService.debug(`${LOG_PREFIX} Slip pay-in accepted.`, {
    code: 'SS_SLIP_PAYIN_ACCEPTED',
    ...getBetslipLogDetails(betslip),
  })
  closeNotificationById(SLIP_ACTION_PENDING_NOTIFICATION_ID)
  recordBetslipPayinTransaction(betslip)

  try {
    await printSlip(betslip)
    logService.info(`${LOG_PREFIX} Slip successfully printed.`, {
      code: 'SS_SLIP_PAYIN_PRINT_SUCCESS',
      ...getBetslipLogDetails(betslip),
    })
    showNotification({
      variant: 'success',
      text: [t('slip_payin_success')],
    })
  } catch (error) {
    logService.error(`${LOG_PREFIX} Failed to print slip.`, {
      code: 'SS_SLIP_PAYIN_PRINT_ERROR',
      ...errorParser.parseUpstream(error),
      ...getBetslipLogDetails(betslip),
    })
    showNotification({
      variant: 'error',
      text: [t('slip_print_failed')],
    })
  }
}

const handleSlipPayinError = (error: unknown) => {
  logService.error(`${LOG_PREFIX} Slip pay-in has failed.`, {
    code: 'ST_SLIP_PAYIN_FAILED',
    ...errorParser.parseUpstream(error),
    ticket_code: error instanceof TicketResolveError ? error.options.context.betslipId : null,
    feBetslipId: error instanceof TicketResolveError ? error.options.context.feBetslipId : null,
    details: error instanceof TicketResolveError ? toRaw(error.options.context.errorDetails) : null,
  })
}

const printSlip = async (betslip: BetslipView) => {
  const { linkCode } = betslip.context
  const printContext: PrintContextPayinSlip = {
    meta: {
      requestId: uuidv4(),
      productDisplayId: linkCode,
    },
    model: {
      slip: betslip,
    },
  }

  logService.debug(`${LOG_PREFIX} Sending template context data to slip print`, {
    code: 'SS_SLIP_PAYIN_PRINT_TEMPLATE_CONTEXT_SUCCESS',
    details: {
      meta: printContext.meta,
      model: {
        slip: toRaw(betslip),
      },
    },
  })

  await printService
    .getPrintService()
    .performPrint(`games${linkCode}`, 'betslip_payin', printContext)
}

export { registerSlipPayinHandlers, handleSlipPayinSuccess, handleSlipPayinError }
